export const STICKY_GLASS_CHROME_CLASSNAME =
  'border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70';

export const STICKY_GLASS_HEADER_SHADOW_CLASSNAME = 'shadow-md';

export const STICKY_GLASS_FOOTER_SHADOW_CLASSNAME =
  'shadow-[0_-4px_6px_-1px_rgb(0_0_0_/_0.1),0_-2px_4px_-2px_rgb(0_0_0_/_0.1)]';

const SCROLL_EDGE_EPSILON_PX = 1;

export interface ElementScrollEdges {
  canScrollUp: boolean;
  canScrollDown: boolean;
}

export function isElementScrollable(element: HTMLElement): boolean {
  return element.scrollHeight - element.clientHeight > SCROLL_EDGE_EPSILON_PX;
}

export function getElementScrollEdges(element: HTMLElement): ElementScrollEdges {
  const maxScrollTop = element.scrollHeight - element.clientHeight;
  if (maxScrollTop <= SCROLL_EDGE_EPSILON_PX) {
    return { canScrollUp: false, canScrollDown: false };
  }

  const scrollTop = element.scrollTop;
  return {
    canScrollUp: scrollTop > SCROLL_EDGE_EPSILON_PX,
    canScrollDown: scrollTop < maxScrollTop - SCROLL_EDGE_EPSILON_PX,
  };
}

export function findOverflowAncestor(element: HTMLElement | null): HTMLElement | null {
  let node = element;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    if (
      style.overflow === 'auto' ||
      style.overflowY === 'auto' ||
      style.overflow === 'scroll' ||
      style.overflowY === 'scroll'
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function observeElementScrollEdges(
  element: HTMLElement,
  onChange: (edges: ElementScrollEdges) => void
): () => void {
  const updateEdges = (): void => {
    onChange(getElementScrollEdges(element));
  };

  updateEdges();

  element.addEventListener('scroll', updateEdges, { passive: true });

  if (typeof ResizeObserver === 'undefined') {
    return () => {
      element.removeEventListener('scroll', updateEdges);
    };
  }

  const resizeObserver = new ResizeObserver(updateEdges);
  const observeTargets = (): void => {
    resizeObserver.disconnect();
    resizeObserver.observe(element);
    for (const child of Array.from(element.children)) {
      resizeObserver.observe(child);
    }
  };
  observeTargets();

  const mutationObserver = new MutationObserver(() => {
    observeTargets();
    updateEdges();
  });
  mutationObserver.observe(element, { childList: true, subtree: true });

  return () => {
    element.removeEventListener('scroll', updateEdges);
    resizeObserver.disconnect();
    mutationObserver.disconnect();
  };
}

export function observeElementScrollable(
  element: HTMLElement,
  onChange: (scrollable: boolean) => void
): () => void {
  return observeElementScrollEdges(element, (edges) => {
    onChange(edges.canScrollUp || edges.canScrollDown);
  });
}
